<script setup lang="ts">
/**
 * 合作平台（`/platforms`；批141，主人裁示 25／26／35／37／41）——**營運主管以上**。
 *
 * 「合作平台」＝以機器身分（每請求 Ed25519 簽章）替旗下每個組織申請與領取授權的多租戶
 * 系統。每個平台組織在 LS 對應一個客戶與一個 instance；平台客戶**不給 portal**、不可刪除
 * 或合併（裁示 24／25）。
 *
 * 版型與〈產品〉頁同一套：左清單＋右三頁籤【基本資料】【金鑰】【組織】。
 *
 * - **基本資料**：名稱、可代辦產品、三個限速覆寫、備註；停權／復權是一顆獨立按鈕
 *   （停權下一個請求就生效：平台 API 與帶平台標頭的心跳一律 401、續期暫停，裁示 41）。
 * - **金鑰**：換鑰三步——設新公鑰（兩把都驗得過）→ 平台改用新私鑰 → 新公鑰生效
 *   （舊私鑰立即失效）；設錯了可撤回。
 * - **組織**：旗下組織（新到舊、「載入更多」續頁），含試用與正式證的數量；
 *   **解除關聯**不可逆，要逐字輸入組織代號確認（裁示 35：平台 API 不提供關閉組織）。
 *
 * 權限在後端（`require_manager`，連讀取都是）；路由 `meta.roles: MANAGER`。
 *
 * 批155：基本資料多一個**自動核准**開關（雙層開關的平台層）。它**不跟著「儲存」走**——
 * 切換當下就確認＋送出（稽核 `platform.auto_approve_set` 一次只記這一件事）；營運主管以上才能切，
 * 其餘身分停用並在固定位置說明原因（這一頁本來就只開給營運主管以上，這是防呆）。
 */
import { computed, onMounted, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as platforms from '@/api/platforms'
import type { LimitField, Platform, PlatformTenant } from '@/api/platforms'
import { LIMIT_FIELDS } from '@/api/platforms'
import { knownErrorText } from '@/lib/apiErrors'
import { vEnter } from '@/lib/enterKey'
import { ROLE_LABELS } from '@/lib/roles'
import { fmtSeen } from '@/lib/staleness'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const auth = useAuthStore()
const dialogs = useDialogsStore()

const LIMIT_LABELS: Record<LimitField, string> = {
  realm_limit_per_min: '平台 API（每分鐘）',
  heartbeat_limit_per_min: '心跳（每分鐘）',
  activate_limit_per_min: '啟用（每分鐘）',
}
const STATUS_LABELS: Record<string, string> = { active: '啟用中', suspended: '已停權' }

const rows = ref<Platform[]>([])
const limitDefaults = ref<Record<LimitField, number> | null>(null)
const loading = ref(false)
const loadError = ref('')
const selectedId = ref('')
const selected = computed(() => rows.value.find((p) => p.id === selectedId.value) ?? null)

type Tab = 'basic' | 'keys' | 'tenants'
const tab = ref<Tab>('basic')
const TABS: { key: Tab; label: string }[] = [
  { key: 'basic', label: '基本資料' },
  { key: 'keys', label: '金鑰' },
  { key: 'tenants', label: '組織' },
]

function detailOf(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}
function errText(e: unknown, fallback: string): string {
  const d = detailOf(e)
  const known = knownErrorText(d)
  if (known) return known
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

async function load(keep = true) {
  loading.value = true
  try {
    const res = await platforms.listPlatforms()
    rows.value = res.items
    limitDefaults.value = res.limit_defaults
    loadError.value = ''
    if (!keep || !rows.value.some((p) => p.id === selectedId.value)) {
      selectedId.value = rows.value[0]?.id ?? ''
    }
  } catch (e) {
    loadError.value = errText(e, '讀取合作平台失敗')
  } finally {
    loading.value = false
  }
}
/** 單筆寫入之後就地換掉那一列（不必整份重載；統計數字後端已經重算）。 */
function replaceRow(p: Platform) {
  const i = rows.value.findIndex((x) => x.id === p.id)
  if (i >= 0) rows.value[i] = p
  else rows.value.push(p)
}

onMounted(async () => {
  void store.ensureProducts().catch(() => {})
  await load(false)
})

function productNames(keys: string[]): string {
  return keys.map((k) => store.productName(k)).join('、') || '—'
}
function limitPlaceholder(k: LimitField): string {
  const d = limitDefaults.value?.[k]
  return d == null ? '留空＝系統預設' : `留空＝系統預設 ${d}`
}

// ---------------------------------------------------------------------------
// ① 基本資料
// ---------------------------------------------------------------------------
interface BasicDraft {
  name: string
  product_keys: string[]
  limits: Record<LimitField, string>
  notes: string
}
function emptyLimits(): Record<LimitField, string> {
  return { realm_limit_per_min: '', heartbeat_limit_per_min: '', activate_limit_per_min: '' }
}
const basic = ref<BasicDraft>({ name: '', product_keys: [], limits: emptyLimits(), notes: '' })
function resetBasic() {
  const p = selected.value
  basic.value = {
    name: p?.name ?? '',
    product_keys: [...(p?.product_keys ?? [])],
    limits: {
      realm_limit_per_min: p?.realm_limit_per_min != null ? String(p.realm_limit_per_min) : '',
      heartbeat_limit_per_min: p?.heartbeat_limit_per_min != null ? String(p.heartbeat_limit_per_min) : '',
      activate_limit_per_min: p?.activate_limit_per_min != null ? String(p.activate_limit_per_min) : '',
    },
    notes: p?.notes ?? '',
  }
}
// 換平台＝草稿與組織清單全部重設；同一個平台的列被換掉（存檔後）只重設草稿。
watch(selected, (p, old) => {
  resetBasic()
  if (p?.id === old?.id) return
  nextKeyInput.value = ''
  tenants.value = []
  tenantCursor.value = null
  tenantsLoaded.value = false
  if (tab.value === 'tenants' && p) void loadTenants(true)
})
watch(tab, (t) => {
  if (t === 'tenants' && selected.value && !tenantsLoaded.value) void loadTenants(true)
})

/** 限速欄：空白＝null（沿用系統預設），其餘必須是正整數。 */
function limitValue(s: string): number | null | 'bad' {
  const t = String(s ?? '').trim()
  if (!t) return null
  return /^\d+$/.test(t) && Number(t) >= 1 ? Number(t) : 'bad'
}
const basicInvalid = computed(() => {
  if (!basic.value.name.trim()) return '名稱不可空白'
  if (!basic.value.product_keys.length) return '至少要選一個可代辦的產品'
  for (const k of LIMIT_FIELDS) if (limitValue(basic.value.limits[k]) === 'bad') return `「${LIMIT_LABELS[k]}」要填正整數或留空`
  return null
})

function toggleProduct(list: string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key]
}

const basicSaving = ref(false)
async function saveBasic() {
  const p = selected.value
  if (!p || basicInvalid.value) return
  const removed = p.product_keys.filter((k) => !basic.value.product_keys.includes(k))
  if (removed.length) {
    const ok = await dialogs.confirm({
      title: '移除可代辦的產品',
      message:
        `之後平台不能再替旗下組織申請「${productNames(removed)}」的授權。` +
        '已經核發的證不受影響（照常續用到期）。確定要修改嗎？',
      confirmText: '修改',
    })
    if (!ok) return
  }
  basicSaving.value = true
  try {
    const limits: Partial<Record<LimitField, number | null>> = {}
    for (const k of LIMIT_FIELDS) limits[k] = limitValue(basic.value.limits[k]) as number | null
    replaceRow(
      await platforms.updatePlatform(p.id, {
        name: basic.value.name.trim(),
        product_keys: basic.value.product_keys,
        ...limits,
        notes: basic.value.notes.trim() || null,
      }),
    )
    resetBasic()
    await dialogs.notify({ title: '已儲存', message: '平台設定已更新，下一個請求就生效。' })
  } catch (e) {
    await fail(e, '儲存平台設定失敗')
  } finally {
    basicSaving.value = false
  }
}

const statusBusy = ref(false)
async function toggleStatus() {
  const p = selected.value
  if (!p) return
  const suspend = p.status !== 'suspended'
  const ok = await dialogs.confirm({
    title: suspend ? `停權「${p.name}」` : `復權「${p.name}」`,
    message: suspend
      ? '停權下一個請求就生效：這個平台的 API 一律拒絕，旗下組織帶平台標頭的心跳也一律拒絕、' +
        '續期暫停（站台會依各自的離線寬限運作）。已核發的證不會被撤銷；復權或換鑰後自動恢復。確定要停權嗎？'
      : '復權之後平台 API 與旗下組織的心跳立即恢復。確定要復權嗎？',
    danger: suspend,
    confirmText: suspend ? '停權' : '復權',
  })
  if (!ok) return
  statusBusy.value = true
  try {
    replaceRow(await platforms.updatePlatform(p.id, { status: suspend ? 'suspended' : 'active' }))
  } catch (e) {
    await fail(e, suspend ? '停權失敗' : '復權失敗')
  } finally {
    statusBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// ①-b 自動核准（批155；雙層開關的平台層）
// ---------------------------------------------------------------------------
const autoApproveBusy = ref(false)
/**
 * 勾選框的 change：先把畫面上的勾選退回原狀，確認之後才送出；成功後由 `replaceRow` 換上
 * 伺服器回來的值（取消或失敗時畫面維持原狀，不會出現「看起來開了其實沒開」）。
 */
async function onAutoApproveChange(ev: Event) {
  const input = ev.target as HTMLInputElement
  const p = selected.value
  const want = input.checked
  input.checked = !want
  if (!p || !auth.isManagerOrAbove) return
  const ok = await dialogs.confirm({
    title: want ? `開啟「${p.name}」的自動核准` : `關閉「${p.name}」的自動核准`,
    message: want
      ? '開啟之後，這個平台送來的申請只要同時符合下面幾件事就會當場核准、直接簽發，不經人工審核：' +
        '申請的套裝也開了「自動核准」、送件內容逐字等於套裝模板、期限在允許的清單內、續約只預先一期。' +
        '任一不符照常轉人工審核。這次切換會記在稽核紀錄裡。確定要開啟嗎？'
      : '關閉之後，這個平台送來的申請一律轉人工審核（已經核發的證不受影響）。這次切換會記在稽核紀錄裡。確定要關閉嗎？',
    danger: want,
    confirmText: want ? '開啟' : '關閉',
  })
  if (!ok) return
  autoApproveBusy.value = true
  try {
    replaceRow(await platforms.updatePlatform(p.id, { auto_approve: want }))
  } catch (e) {
    await fail(e, want ? '開啟自動核准失敗' : '關閉自動核准失敗')
  } finally {
    autoApproveBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// ② 金鑰（換鑰三步）
// ---------------------------------------------------------------------------
const nextKeyInput = ref('')
const keyBusy = ref<'' | 'set' | 'promote' | 'drop'>('')
/** 前端先擋明顯不對的形狀（44 字元標準 base64）；權威在後端（還會驗是不是 Ed25519 公鑰）。 */
const KEY_RE = /^[A-Za-z0-9+/]{43}=$/
const nextKeyOk = computed(() => KEY_RE.test(nextKeyInput.value.trim()))

async function setNext() {
  const p = selected.value
  if (!p || !nextKeyOk.value) return
  keyBusy.value = 'set'
  try {
    replaceRow(await platforms.setNextKey(p.id, nextKeyInput.value.trim()))
    nextKeyInput.value = ''
  } catch (e) {
    await fail(e, '設定新公鑰失敗')
  } finally {
    keyBusy.value = ''
  }
}
async function promote() {
  const p = selected.value
  if (!p?.public_key_next) return
  const ok = await dialogs.confirm({
    title: '新公鑰生效',
    message:
      `新公鑰（${p.public_key_next_fp ?? '—'}）生效之後，舊私鑰簽的請求一律拒絕。` +
      '請先確認平台已經改用新私鑰簽章（可看最後活動時間是否持續更新）。確定要生效嗎？',
    danger: true,
    confirmText: '生效',
  })
  if (!ok) return
  keyBusy.value = 'promote'
  try {
    replaceRow(await platforms.promoteNextKey(p.id))
  } catch (e) {
    await fail(e, '新公鑰生效失敗')
  } finally {
    keyBusy.value = ''
  }
}
async function dropNext() {
  const p = selected.value
  if (!p?.public_key_next) return
  const ok = await dialogs.confirm({
    title: '撤回新公鑰',
    message: '撤回之後只剩現行公鑰驗得過；平台若已經改用新私鑰，請求會立即被拒絕。確定要撤回嗎？',
    danger: true,
    confirmText: '撤回',
  })
  if (!ok) return
  keyBusy.value = 'drop'
  try {
    replaceRow(await platforms.dropNextKey(p.id))
  } catch (e) {
    await fail(e, '撤回新公鑰失敗')
  } finally {
    keyBusy.value = ''
  }
}
/** 換鑰進行到第幾步（1＝還沒設新公鑰；2＝兩把都驗得過、等平台切換）。 */
const keyStep = computed(() => (selected.value?.public_key_next ? 2 : 1))

// ---------------------------------------------------------------------------
// ③ 組織
// ---------------------------------------------------------------------------
const tenants = ref<PlatformTenant[]>([])
const tenantCursor = ref<string | null>(null)
const tenantsLoaded = ref(false)
const tenantsBusy = ref(false)
const includeDetached = ref(false)
const TENANT_PAGE = 50

async function loadTenants(reset: boolean) {
  const p = selected.value
  if (!p) return
  tenantsBusy.value = true
  try {
    const page = await platforms.listPlatformTenants(p.id, {
      after: reset ? null : tenantCursor.value,
      limit: TENANT_PAGE,
      includeDetached: includeDetached.value,
    })
    tenants.value = reset ? page.items : [...tenants.value, ...page.items]
    tenantCursor.value = page.next_cursor
    tenantsLoaded.value = true
  } catch (e) {
    await fail(e, '讀取組織清單失敗')
  } finally {
    tenantsBusy.value = false
  }
}
watch(includeDetached, () => void loadTenants(true))

const detachTarget = ref<PlatformTenant | null>(null)
const detachTyped = ref('')
const detachBusy = ref(false)
function openDetach(t: PlatformTenant) {
  detachTarget.value = t
  detachTyped.value = ''
}
async function doDetach() {
  const p = selected.value
  const t = detachTarget.value
  if (!p || !t || detachTyped.value.trim() !== t.external_ref) return
  detachBusy.value = true
  try {
    const res = await platforms.detachPlatformTenant(p.id, t.id, detachTyped.value.trim())
    detachTarget.value = null
    await loadTenants(true)
    await load()
    await dialogs.notify({
      title: '已解除關聯',
      message:
        `組織「${t.external_ref}」已解除關聯：撤銷 ${res.revoked_licenses} 張授權` +
        (res.revoked_addons ? `、${res.revoked_addons} 張加購` : '') +
        (res.cancelled_requests ? `、撤回 ${res.cancelled_requests} 筆申請` : '') +
        '。這個客戶與站台識別之後不能再被任何平台認領。',
    })
  } catch (e) {
    await fail(e, '解除關聯失敗')
  } finally {
    detachBusy.value = false
  }
}

// ---------------------------------------------------------------------------
// 登錄平台
// ---------------------------------------------------------------------------
const createOpen = ref(false)
const createBusy = ref(false)
const createForm = ref({
  platform_key: '',
  name: '',
  product_keys: [] as string[],
  public_key: '',
  limits: emptyLimits(),
  notes: '',
})
const createError = ref<{ field?: string; message: string } | null>(null)
function openCreate() {
  createForm.value = {
    platform_key: '',
    name: '',
    product_keys: [],
    public_key: '',
    limits: emptyLimits(),
    notes: '',
  }
  createError.value = null
  createOpen.value = true
}
const PLATFORM_KEY_RE = /^[a-z0-9][a-z0-9-]{1,31}$/
const createInvalid = computed(() => {
  const f = createForm.value
  if (!PLATFORM_KEY_RE.test(f.platform_key.trim())) return '平台代號只能用小寫英數與連字號、英數開頭、2–32 字（不可有點）'
  if (!f.name.trim()) return '名稱不可空白'
  if (!f.product_keys.length) return '至少要選一個可代辦的產品'
  if (!KEY_RE.test(f.public_key.trim())) return '公鑰要是 44 字元的標準 base64（raw 32 bytes Ed25519 公鑰）'
  for (const k of LIMIT_FIELDS) if (limitValue(f.limits[k]) === 'bad') return `「${LIMIT_LABELS[k]}」要填正整數或留空`
  return null
})
async function doCreate() {
  if (createInvalid.value) return
  const f = createForm.value
  createBusy.value = true
  createError.value = null
  try {
    const limits: Partial<Record<LimitField, number | null>> = {}
    for (const k of LIMIT_FIELDS) limits[k] = limitValue(f.limits[k]) as number | null
    const p = await platforms.createPlatform({
      platform_key: f.platform_key.trim(),
      name: f.name.trim(),
      product_keys: f.product_keys,
      public_key: f.public_key.trim(),
      ...limits,
      notes: f.notes.trim() || null,
    })
    createOpen.value = false
    await load()
    selectedId.value = p.id
    tab.value = 'basic'
  } catch (e) {
    const d = detailOf(e)
    if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
      createError.value = {
        field: typeof (d as { field?: unknown }).field === 'string' ? (d as { field: string }).field : undefined,
        message: (d as { message: string }).message,
      }
    } else {
      await fail(e, '登錄平台失敗')
    }
  } finally {
    createBusy.value = false
  }
}

const productOptions = computed(() => store.products.map((p) => ({ key: p.key, name: p.name })))
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>合作平台</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <UiButton variant="primary" @click="openCreate">＋ 登錄平台</UiButton>
    </div>

    <p class="subtle intro">
      合作平台以機器身分（每個請求都帶數位簽章）替旗下的組織申請與領取授權；每個組織在這裡對應一個客戶。
      平台的組織<strong>不使用客戶 portal</strong>，也不能刪除或合併。
    </p>

    <div v-if="loading && !rows.length" class="loading-wrap"><span class="spinner lg" /></div>
    <p v-else-if="loadError" class="err">{{ loadError }}</p>

    <div v-else class="grid">
      <!-- 左：平台清單 -->
      <div class="card">
        <div class="card-head"><h3 class="section-title">平台</h3></div>
        <ul class="list">
          <li
            v-for="p in rows"
            :key="p.id"
            :class="{ sel: selectedId === p.id }"
            @click="selectedId = p.id"
          >
            <span class="pname">{{ p.name }}</span>
            <code class="mono">{{ p.platform_key }}</code>
            <small class="cnt">
              組織 {{ p.stats.tenants }}・試用中 {{ p.stats.trials_active }}・正式 {{ p.stats.licensed }}
              <span v-if="p.status === 'suspended'" class="sus-tag">已停權</span>
              <span v-if="p.public_key_next" class="rot-tag">換鑰中</span>
              <span v-if="p.auto_approve" class="aa-tag">自動核准</span>
            </small>
          </li>
        </ul>
        <div v-if="!rows.length" class="empty">
          <div class="empty-sub">還沒有登錄任何合作平台。</div>
        </div>
      </div>

      <!-- 右：三頁籤 -->
      <div v-if="selected" class="card">
        <div class="tabs" data-hscroll>
          <button
            v-for="t in TABS"
            :key="t.key"
            type="button"
            class="tab"
            :class="{ active: tab === t.key }"
            @click="tab = t.key"
          >
            {{ t.label }}
          </button>
        </div>

        <!-- ① 基本資料 -->
        <template v-if="tab === 'basic'">
          <div class="stat-row">
            <span class="status-pill" :class="selected.status === 'suspended' ? 'revoked' : 'active'">
              {{ STATUS_LABELS[selected.status] ?? selected.status }}
            </span>
            <span class="subtle">最後活動 {{ fmtSeen(selected.last_seen_at) }}</span>
            <span class="subtle">
              組織 {{ selected.stats.tenants }}（已解除 {{ selected.stats.tenants_detached }}）・
              試用中 {{ selected.stats.trials_active }}・領過試用 {{ selected.stats.trials_issued }}・
              正式 {{ selected.stats.licensed }}
            </span>
          </div>
          <div class="form-grid two">
            <label class="field">
              <span class="field-label">平台代號</span>
              <input :value="selected.platform_key" class="mono" disabled />
              <small class="subtle">建立後不可更改——它是平台簽章輸入的一部分。</small>
            </label>
            <label class="field">
              <span class="field-label">名稱</span>
              <input v-model="basic.name" maxlength="120" />
            </label>
          </div>
          <div class="field block">
            <span class="field-label">可代辦的產品</span>
            <div class="checks">
              <label v-for="o in productOptions" :key="o.key" class="chk">
                <input
                  type="checkbox"
                  :checked="basic.product_keys.includes(o.key)"
                  @change="basic.product_keys = toggleProduct(basic.product_keys, o.key)"
                />
                {{ o.name }}
              </label>
              <span v-if="!productOptions.length" class="subtle">產品清單載入中…</span>
            </div>
          </div>
          <!-- 批155：雙層開關的平台層。切換當下確認＋送出，不跟著下面的「儲存」走。
               說明文字固定在勾選框下面（兩種狀態同一段），切換時版面不動。 -->
          <div class="card-head sub"><h3 class="section-title">自動核准</h3></div>
          <div class="aa-row">
            <label class="chk" :class="{ off: !auth.isManagerOrAbove }">
              <input
                type="checkbox"
                :checked="!!selected.auto_approve"
                :disabled="!auth.isManagerOrAbove || autoApproveBusy"
                @change="onAutoApproveChange"
              />
              平台送來的申請可以自動核准
            </label>
            <span class="status-pill" :class="selected.auto_approve ? 'active' : 'superseded'">
              {{ selected.auto_approve ? '已開啟' : '已關閉' }}
            </span>
          </div>
          <p class="subtle aa-note">
            還要同時符合：申請的套裝也打開「自動核准」、送件內容逐字等於套裝模板、期限在允許的清單內、
            續約只預先一期——任一不符照常轉人工審核。內部套裝一律人工審核。每次切換都會記在稽核紀錄裡。
          </p>
          <p v-if="!auth.isManagerOrAbove" class="subtle aa-note">
            只有「{{ ROLE_LABELS.manager }}」以上能切換這個開關。
          </p>

          <div class="card-head sub"><h3 class="section-title">限速覆寫</h3></div>
          <div class="form-grid three">
            <label v-for="k in LIMIT_FIELDS" :key="k" class="field">
              <span class="field-label">{{ LIMIT_LABELS[k] }}</span>
              <input v-model="basic.limits[k]" inputmode="numeric" :placeholder="limitPlaceholder(k)" />
              <small class="subtle">目前生效：{{ selected.effective_limits[k] }}</small>
            </label>
          </div>
          <label class="field block">
            <span class="field-label">備註</span>
            <textarea v-model="basic.notes" rows="3" maxlength="2000" />
          </label>
          <p v-if="basicInvalid" class="err">{{ basicInvalid }}</p>
          <div class="acts-row">
            <UiButton class="left-btn" :loading="statusBusy" @click="toggleStatus">
              {{ selected.status === 'suspended' ? '復權' : '停權…' }}
            </UiButton>
            <UiButton @click="resetBasic">還原</UiButton>
            <UiButton variant="primary" :loading="basicSaving" :disabled="!!basicInvalid" @click="saveBasic">
              儲存
            </UiButton>
          </div>
        </template>

        <!-- ② 金鑰 -->
        <template v-else-if="tab === 'keys'">
          <div class="field block">
            <span class="field-label">現行公鑰</span>
            <code class="keybox">{{ selected.public_key }}</code>
            <small class="subtle">
              指紋 <code class="mono">{{ selected.public_key_fp ?? '—' }}</code>
              <template v-if="selected.key_rotated_at">・上次換鑰 {{ fmtSeen(selected.key_rotated_at) }}</template>
            </small>
          </div>

          <div class="card-head sub"><h3 class="section-title">換鑰</h3></div>
          <ol class="steps">
            <li :class="{ done: keyStep > 1, on: keyStep === 1 }">
              <div class="step-title">① 設定新公鑰</div>
              <template v-if="keyStep === 1">
                <div class="step-row">
                  <input
                    v-model="nextKeyInput"
                    class="mono keyin"
                    placeholder="貼上平台提供的新公鑰（44 字元 base64）"
                    maxlength="64"
                    v-enter="setNext"
                  />
                  <UiButton variant="primary" :disabled="!nextKeyOk" :loading="keyBusy === 'set'" @click="setNext">
                    設定新公鑰
                  </UiButton>
                </div>
                <small v-if="nextKeyInput && !nextKeyOk" class="err">公鑰要是 44 字元的標準 base64。</small>
              </template>
              <template v-else>
                <code class="keybox">{{ selected.public_key_next }}</code>
                <small class="subtle">指紋 <code class="mono">{{ selected.public_key_next_fp ?? '—' }}</code></small>
              </template>
            </li>
            <li :class="{ on: keyStep === 2 }">
              <div class="step-title">② 平台改用新私鑰</div>
              <p class="subtle">這段期間現行與新公鑰<strong>兩把都驗得過</strong>，平台可以從容切換。</p>
            </li>
            <li :class="{ on: keyStep === 2 }">
              <div class="step-title">③ 新公鑰生效</div>
              <p class="subtle">生效之後舊私鑰簽的請求一律拒絕。</p>
              <div v-if="keyStep === 2" class="step-row">
                <UiButton :loading="keyBusy === 'drop'" @click="dropNext">撤回新公鑰…</UiButton>
                <UiButton variant="primary" :loading="keyBusy === 'promote'" @click="promote">新公鑰生效…</UiButton>
              </div>
            </li>
          </ol>
        </template>

        <!-- ③ 組織 -->
        <template v-else>
          <div class="card-head sub">
            <h3 class="section-title">組織</h3>
            <span class="spacer" />
            <label class="chk">
              <input v-model="includeDetached" type="checkbox" />
              含已解除關聯
            </label>
          </div>
          <div class="tbl-scroll" data-hscroll>
            <table v-card-table class="tbl card-table">
              <thead>
                <tr>
                  <th>組織代號</th><th>客戶</th><th>站台識別</th><th>現行授權</th>
                  <th>待審申請</th><th>建立</th><th>狀態</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in tenants" :key="t.id" :class="{ gone: !!t.detached_at }">
                  <td class="nowrap"><code class="mono">{{ t.external_ref }}</code></td>
                  <td>
                    {{ t.customer_name ?? '—' }}
                    <div v-if="t.contact_email" class="subtle sub">{{ t.contact_email }}</div>
                  </td>
                  <td class="nowrap">
                    <code class="mono" :title="t.instance_id">{{ t.instance_id.slice(0, 12) }}</code>
                  </td>
                  <td>
                    <template v-if="t.license">
                      <div class="nowrap" :title="store.productName(t.license.product)">
                        <code class="mono">{{ t.license.license_id }}</code>
                        <span v-if="t.license.trial" class="chip trial">試用</span>
                      </div>
                      <div class="subtle sub nowrap">到期 {{ fmtSeen(t.license.expires_at, { short: true }) }}</div>
                    </template>
                    <span v-else class="subtle">—</span>
                  </td>
                  <td class="nowrap">
                    <span v-if="t.pending_request" class="chip">審核中</span>
                    <span v-else class="subtle">—</span>
                  </td>
                  <td class="nowrap subtle">{{ fmtSeen(t.created_at, { short: true }) }}</td>
                  <td class="nowrap">
                    <span v-if="t.detached_at" class="status-pill superseded">已解除 {{ fmtSeen(t.detached_at, { short: true }) }}</span>
                    <span v-else class="status-pill active">在役</span>
                  </td>
                  <td class="acts-cell">
                    <UiButton v-if="!t.detached_at" size="sm" @click="openDetach(t)">解除關聯…</UiButton>
                  </td>
                </tr>
                <tr v-if="tenantsLoaded && !tenants.length">
                  <td colspan="8" class="subtle empty-row">
                    {{ includeDetached ? '這個平台還沒有任何組織。' : '這個平台目前沒有在役的組織。' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="more-row">
            <span v-if="tenantsBusy" class="subtle"><span class="spinner" /> 載入中…</span>
            <UiButton v-else-if="tenantCursor" size="sm" @click="loadTenants(false)">載入更多</UiButton>
          </div>
        </template>
      </div>
      <div v-else class="card">
        <div class="empty"><div class="empty-sub">請先在左側選一個平台。</div></div>
      </div>
    </div>

    <!-- 登錄平台 -->
    <UiModal :open="createOpen" title="登錄合作平台" width="680px" @close="createOpen = false">
      <p v-if="createError" class="err">
        {{ createError.field ? `欄位 ${createError.field}：` : '' }}{{ createError.message }}
      </p>
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">平台代號</span>
          <input
            v-model="createForm.platform_key"
            class="mono"
            placeholder="例：acme-saas"
            maxlength="32"
            v-enter="doCreate"
          />
          <small class="subtle">小寫英數與連字號、2–32 字、不可有點；建立後不可更改。</small>
        </label>
        <label class="field">
          <span class="field-label">名稱</span>
          <input v-model="createForm.name" maxlength="120" v-enter="doCreate" />
        </label>
      </div>
      <label class="field block">
        <span class="field-label">公鑰</span>
        <input
          v-model="createForm.public_key"
          class="mono"
          placeholder="平台提供的 Ed25519 公鑰（44 字元 base64）"
          maxlength="64"
          v-enter="doCreate"
        />
      </label>
      <div class="field block">
        <span class="field-label">可代辦的產品</span>
        <div class="checks">
          <label v-for="o in productOptions" :key="o.key" class="chk">
            <input
              type="checkbox"
              :checked="createForm.product_keys.includes(o.key)"
              @change="createForm.product_keys = toggleProduct(createForm.product_keys, o.key)"
            />
            {{ o.name }}
          </label>
        </div>
      </div>
      <div class="form-grid three block">
        <label v-for="k in LIMIT_FIELDS" :key="k" class="field">
          <span class="field-label">{{ LIMIT_LABELS[k] }}</span>
          <input v-model="createForm.limits[k]" inputmode="numeric" :placeholder="limitPlaceholder(k)" />
        </label>
      </div>
      <label class="field block">
        <span class="field-label">備註</span>
        <textarea v-model="createForm.notes" rows="2" maxlength="2000" />
      </label>
      <p v-if="createInvalid" class="subtle">{{ createInvalid }}</p>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!!createInvalid" :loading="createBusy" @click="doCreate">登錄</UiButton>
      </template>
    </UiModal>

    <!-- 解除關聯（不可逆） -->
    <UiModal
      :open="!!detachTarget"
      title="解除組織關聯"
      width="560px"
      @close="detachTarget = null"
    >
      <template v-if="detachTarget">
        <p>
          即將解除平台「{{ selected?.name }}」的組織
          <code class="mono">{{ detachTarget.external_ref }}</code>（{{ detachTarget.customer_name ?? '—' }}）。
        </p>
        <ul class="warn-list">
          <li>撤回這個組織所有審核中的申請。</li>
          <li>撤銷這個組織全部生效中的授權（含加購）——站台會在下次心跳時收到撤銷。</li>
          <li>這個客戶與站台識別<strong>之後不能再被任何平台認領</strong>；組織代號本身可以被平台重新使用。</li>
          <li>此動作<strong>不可逆</strong>。</li>
        </ul>
        <label class="field">
          <span class="field-label">請輸入 <code class="mono">{{ detachTarget.external_ref }}</code> 確認</span>
          <input v-model="detachTyped" class="mono" placeholder="組織代號" v-enter="doDetach" />
        </label>
      </template>
      <template #foot>
        <UiButton @click="detachTarget = null">取消</UiButton>
        <UiButton
          variant="danger"
          :loading="detachBusy"
          :disabled="!detachTarget || detachTyped.trim() !== detachTarget.external_ref"
          @click="doDetach"
        >
          解除關聯
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
/* ★ 右欄要 `minmax(0, 1fr)`＋卡片 `min-width: 0`（批141-R）：`1fr` 的下限是內容的
   min-content，〈組織〉表 8 欄在 1440 寬會把整張卡片撐出畫面、右緣被裁掉；收住之後
   表格改由卡片內的 `.tbl-scroll` 橫捲（與其他寬表格同一套）。 */
.grid {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: var(--sp-4);
  align-items: start;
}
.grid > .card {
  min-width: 0;
}
/* 批155：窄螢幕（768 等）左右兩欄改成上下疊——右卡只剩不到 200px，輸入框會撐出卡片
   （與〈客戶〉頁同一個 900px 斷點）。 */
@media (max-width: 900px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
/* 左清單：與〈產品〉頁同一套 */
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 2px;
}
.list li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px var(--sp-2);
  padding: 8px 10px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.list li:hover {
  background: var(--bg-hover);
}
.list li.sel {
  background: var(--brand-100);
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .list li.sel {
  background: var(--bg-active);
  color: var(--brand-400);
}
.pname {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list code {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.list .cnt {
  grid-column: 1 / -1;
  font-size: var(--fs-11);
  color: var(--fg-4);
  font-weight: 400;
}
.sus-tag {
  margin-left: 4px;
  color: var(--danger);
}
.rot-tag {
  margin-left: 4px;
  color: var(--warning);
}
.aa-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
}
/* 批155：自動核准開關列（勾選框＋狀態 pill；說明在下面固定位置）。 */
.aa-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.chk.off {
  cursor: not-allowed;
  opacity: 0.6;
}
.aa-note {
  margin: var(--sp-2) 0 0;
  line-height: var(--lh-base);
}
.stat-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  margin: var(--sp-3) 0;
}
.form-grid.two,
.form-grid.three {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.block {
  margin-top: var(--sp-3);
}
.card-head.sub {
  margin-top: var(--sp-4);
}
.checks {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
}
.acts-row {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-4);
}
.acts-row .left-btn {
  margin-right: auto;
}
.keybox {
  display: block;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  font-family: var(--font-mono);
  font-size: var(--fs-12);
  word-break: break-all;
  user-select: all;
}
.steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--sp-3);
}
.steps li {
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  opacity: 0.6;
}
.steps li.on {
  opacity: 1;
  border-color: var(--brand-500);
}
.steps li.done {
  opacity: 1;
}
.steps .subtle {
  margin: 4px 0 0;
}
.step-title {
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg);
  margin-bottom: var(--sp-2);
}
.step-row {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  flex-wrap: wrap;
  margin-top: var(--sp-2);
}
.keyin {
  flex: 1;
  min-width: 260px;
}
.mono {
  font-family: var(--font-mono);
}
.nowrap {
  white-space: nowrap;
}
.sub {
  font-size: var(--fs-11);
}
tr.gone td {
  color: var(--fg-4);
}
.chip.trial {
  margin-left: 4px;
  color: var(--warning);
  border-color: var(--warning);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.more-row {
  display: flex;
  justify-content: center;
  margin-top: var(--sp-3);
}
.warn-list {
  margin: var(--sp-2) 0 var(--sp-3);
  padding-left: 1.2em;
  color: var(--fg-2);
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
</style>
